import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, RotateCcw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const SAMPLE_COUNTER_TEXT = "Online developer and formatting utilities make it incredibly simple to test, count, convert, and inspect code strings directly inside your secure sandbox environment.";

export function WordCounter() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setText(SAMPLE_COUNTER_TEXT);
  }, []);

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const chars = text.length;
  const charsNoSpaces = text.replace(/\s/g, "").length;
  const sentences = text.trim() ? text.split(/[.!?]+/).filter(Boolean).length : 0;
  const paragraphs = text.trim() ? text.split(/\n\n+/).filter(Boolean).length : 0;

  const handleCopy = () => {
    copyToClipboard(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Word Counter"
      description="Live count of words, characters, sentences, and paragraphs in your text."
      h1="Word Counter"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Type or paste your text into the input area above.",
        "The tool will instantly count the words, characters, sentences, and paragraphs.",
        "Use the copy button to copy your text to the clipboard.",
        "Click the clear button to remove all text and start over."
      ]}
      faq={[
        { question: "Is this word counter free?", answer: "Yes, this tool is 100% free." },
        { question: "Is my text private?", answer: "Absolutely. All processing happens locally in your browser." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Words", value: words },
            { label: "Characters", value: chars },
            { label: "Chars (no spaces)", value: charsNoSpaces },
            { label: "Sentences", value: sentences },
            { label: "Paragraphs", value: paragraphs },
          ].map((stat) => (
            <div key={stat.label} className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center shadow-xs">
              <div className="text-3xl font-black text-slate-900">{stat.value}</div>
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        <Textarea
          placeholder="Type or paste your text here..."
          className="min-h-[250px] text-base"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Text"}
          </Button>
          <Button onClick={() => setText(SAMPLE_COUNTER_TEXT)} variant="outline" className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => setText("")} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
